/**
 * Lesson Compile — transforms lesson document into production formats.
 * Outputs: editor.json, content.xml, exercise XMLs, media assets.
 */

import React, { useCallback, useState } from 'react';
import { LessonStagePanel } from '../../shared/LessonStagePanel';
import { useLessonContext } from '../../LessonContext';
import { getPresignedUrl } from '../../../../services/courseRunsService';

function CompileOutput() {
  const { document } = useLessonContext();
  const compiled: any = document?.compiled;

  if (!compiled) {
    return <div className="text-sm text-gray-500">Not yet compiled. Run the compile stage to generate production files.</div>;
  }

  // exercise_keys is a list of {hex_id, key}; tolerate the legacy dict shape for older compiles.
  const exerciseList: Array<{ hex_id: string; key: string }> = Array.isArray(compiled.exercise_keys)
    ? compiled.exercise_keys
    : Object.entries(compiled.exercise_keys || {}).map(([hex_id, key]) => ({ hex_id, key: key as string }));

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Editor Rows" value={compiled.editor_row_count || 0} />
        <StatCard label="Exercises" value={exerciseList.length} />
        <StatCard label="Assets" value={(compiled.asset_keys || []).length} />
        <StatCard label="Status" value="Compiled" color="green" />
      </div>

      {/* File listing */}
      <div className="border rounded divide-y text-xs">
        <FileRow
          label="editor.json"
          s3Key={compiled.editor_key}
          type="application/json"
        />
        <FileRow
          label="content.xml"
          s3Key={compiled.content_key}
          type="application/xml"
        />
        {exerciseList.map(({ hex_id, key }) => (
          <FileRow
            key={hex_id}
            label={`exercises/${hex_id}.xml`}
            s3Key={key}
            type="application/xml"
          />
        ))}
        {(compiled.asset_keys || []).map((key: string) => (
          <FileRow
            key={key}
            label={`_utility/${key.split('/').pop()}`}
            s3Key={key}
            type="media"
          />
        ))}
      </div>

      {/* Exercise ID mapping */}
      {compiled.exercise_id_map && Object.keys(compiled.exercise_id_map).length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Exercise ID Mapping</h4>
          <div className="text-xs bg-gray-50 rounded p-2 font-mono space-y-1">
            {Object.entries(compiled.exercise_id_map).map(([ourId, hexId]) => (
              <div key={ourId} className="flex gap-2">
                <span className="text-gray-500">{ourId}</span>
                <span className="text-gray-400">→</span>
                <span className="text-gray-700">{hexId as string}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="bg-gray-50 rounded p-2.5">
      <div className="text-[10px] font-semibold text-gray-400 uppercase">{label}</div>
      <div className={`text-lg font-bold ${color === 'green' ? 'text-green-600' : 'text-gray-800'}`}>
        {value}
      </div>
    </div>
  );
}

function FileRow({ label, s3Key, type }: { label: string; s3Key: string; type: string }) {
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const url = await getPresignedUrl(s3Key);
      window.open(url, '_blank');
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  }, [s3Key]);

  const handlePreview = useCallback(async () => {
    if (preview) {
      setShowPreview(!showPreview);
      return;
    }
    try {
      const url = await getPresignedUrl(s3Key);
      const resp = await fetch(url);
      const text = await resp.text();
      setPreview(text);
      setShowPreview(true);
    } catch (e) {
      console.error('Preview failed:', e);
    }
  }, [s3Key, preview, showPreview]);

  const isPreviewable = type === 'application/json' || type === 'application/xml';

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2">
        <span className="font-mono text-gray-700">{label}</span>
        <div className="flex items-center gap-2">
          {isPreviewable && (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800"
              onClick={handlePreview}
            >
              {showPreview ? 'Hide' : 'Preview'}
            </button>
          )}
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
            disabled={downloading}
            onClick={handleDownload}
          >
            {downloading ? '...' : 'Download'}
          </button>
        </div>
      </div>
      {showPreview && preview && (
        <div className="px-3 pb-2">
          <pre className="text-[10px] bg-gray-50 rounded p-2 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap break-all">
            {type === 'application/json' ? JSON.stringify(JSON.parse(preview), null, 2).slice(0, 5000) : preview.slice(0, 5000)}
            {preview.length > 5000 && '\n\n... (truncated)'}
          </pre>
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: Record<string, unknown>) {
  const compiled = doc.compiled as any;
  if (!compiled) return <div className="text-sm text-gray-500">Not compiled yet.</div>;
  const exerciseCount = Array.isArray(compiled.exercise_keys)
    ? compiled.exercise_keys.length
    : Object.keys(compiled.exercise_keys || {}).length;
  return (
    <div className="text-xs text-gray-600">
      Compiled: {compiled.editor_row_count} rows, {exerciseCount} exercises, {(compiled.asset_keys || []).length} assets
    </div>
  );
}

export function LessonCompile() {
  const { document } = useLessonContext();
  const hasContent = !!(document?.lesson_content as any)?.sections?.length;

  return (
    <LessonStagePanel
      stageId="lesson_compile"
      title="Compile"
      description="Transform lesson content into production formats (editor.json, content.xml, exercise XML)."
      renderOutput={renderOutput}
      runLabel="Compile Lesson"
      runDisabled={!hasContent}
      runDisabledReason={!hasContent ? 'Generate lesson content first' : undefined}
    >
      <CompileOutput />
    </LessonStagePanel>
  );
}
